import { useState } from "react";

import {
  ChatBubbleLeftRightIcon,
  EyeIcon,
  FlagIcon,
} from "@heroicons/react/24/outline";
import { DateTime } from "luxon";

import Avatar from "../user/Avatar";
import ConversationPartChip from "./ConversationPartChip";
import EbayMessageChain from "./EbayMessageChain";
import Tooltip from "../shared/Tooltip";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";
import stringToColour from "../../utils/stringToColour";
import { IEbayMessage } from "../../models/EbayMessage";

interface EbayMessagingConversationListProps {
  messages: IEbayMessage[] | undefined;
  /** messageId -> array of staff userIds currently reading the message. */
  activeViewers: Record<string, string[]>;
}

const isFlagged = (message: IEbayMessage): boolean => {
  const flagged = message.flagged;
  if (!flagged) return false;
  // Backend returns a mix of booleans and string variants ("true", "True", "1").
  return (
    flagged === true ||
    flagged === 1 ||
    String(flagged).toLowerCase() === "true" ||
    String(flagged) === "1"
  );
};

const isRead = (message: IEbayMessage): boolean => {
  return String(message.read).toLowerCase() === "true";
};

const initialsFor = (name: string): string =>
  name
    .split(" ")
    .filter((word) => RegExp(/^\p{L}/, "u").test(word))
    .map((word, i) => (i < 2 ? word.substring(0, 1).toUpperCase() : ""))
    .join("") || "?";

export default function EbayMessagingConversationList({
  messages,
  activeViewers,
}: EbayMessagingConversationListProps) {
  const [chainOpen, setChainOpen] = useState(false);
  const [chosenMessage, setChosenMessage] = useState<IEbayMessage | undefined>();

  const openChain = (message: IEbayMessage) => {
    setChosenMessage(message);
    setChainOpen(true);
  };

  if (!messages || messages.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center text-sm text-slate-600">
        No messages to show for this window.
      </div>
    );
  }

  return (
    <>
      <EbayMessageChain
        open={chainOpen}
        setOpen={setChainOpen}
        originMessage={chosenMessage}
      />

      <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white shadow-sm">
        {messages.map((message) => {
          const senderName = message.sendingUserID || "Unknown sender";
          const viewers = activeViewers[message.messageID] || [];
          const read = isRead(message);
          const flagged = isFlagged(message);
          const relativeTime = message.receiveDate
            ? DateTime.fromISO(message.receiveDate).toRelative()
            : null;

          return (
            <li
              key={message.messageID}
              onClick={() => openChain(message)}
              className={classNames(
                "group relative flex cursor-pointer gap-3 px-4 py-3 transition-colors",
                read ? "hover:bg-slate-50" : "bg-blue-50/60 hover:bg-blue-50"
              )}
            >
              {!read && (
                <span
                  aria-hidden
                  className="absolute left-0 top-1/2 -translate-y-1/2 h-10 w-1 rounded-r bg-primary-500"
                />
              )}

              <div
                className="mt-0.5 flex h-10 w-10 flex-none items-center justify-center rounded-full text-sm font-semibold text-white"
                style={{ background: stringToColour(senderName) }}
                aria-hidden
              >
                {initialsFor(senderName)}
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p
                        className={classNames(
                          "truncate text-sm",
                          read
                            ? "font-medium text-slate-700"
                            : "font-semibold text-slate-900"
                        )}
                      >
                        {senderName}
                      </p>
                      {!read && (
                        <span className="inline-flex items-center rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-700">
                          New
                        </span>
                      )}
                      {flagged && (
                        <Tooltip content="Flagged for assistance">
                          <FlagIcon className="h-4 w-4 text-rose-500" />
                        </Tooltip>
                      )}
                    </div>
                    <p className="truncate text-xs text-slate-500">
                      {message.itemTitle || "No listing title"}
                    </p>
                  </div>

                  <div className="flex flex-none items-center gap-2">
                    {viewers.length > 0 && (
                      <div className="flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 ring-1 ring-inset ring-emerald-200">
                        <EyeIcon className="h-3.5 w-3.5 text-emerald-600" />
                        <div className="flex -space-x-1">
                          {viewers.slice(0, 3).map((viewerId) => (
                            <Avatar
                              key={viewerId}
                              userId={viewerId}
                              classes="w-5 h-5 rounded-full ring-2 ring-white"
                              textClasses="text-[9px]"
                              tooltipPrefix="Currently viewing"
                            />
                          ))}
                        </div>
                        {viewers.length > 3 && (
                          <span className="text-[10px] font-semibold text-emerald-700">
                            +{viewers.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                    {message.receiveDate && (
                      <span
                        className="whitespace-nowrap text-xs text-slate-500"
                        title={formatDateTime(
                          message.receiveDate,
                          DateTime.DATETIME_SHORT
                        )}
                      >
                        {relativeTime ??
                          formatDateTime(
                            message.receiveDate,
                            DateTime.DATETIME_SHORT
                          )}
                      </span>
                    )}
                  </div>
                </div>

                <p className="mt-1 line-clamp-2 whitespace-pre-line text-sm text-slate-700">
                  {message.body || message.text || ""}
                </p>

                {message.itemID && (
                  <ConversationPartChip
                    itemID={message.itemID}
                    fallbackTitle={message.itemTitle}
                  />
                )}

                <div className="mt-1.5 flex items-center gap-3 text-[11px] text-slate-500">
                  <span className="inline-flex items-center gap-1">
                    <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" />
                    Open conversation
                  </span>
                  {message.messageType && (
                    <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                      {message.messageType}
                    </span>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
}
